import React, { useState, useEffect } from 'react'
import { Supplier } from '../types'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const Suppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null)

  useEffect(() => {
    fetchSuppliers()
  }, [])

  const fetchSuppliers = async () => {
    try {
      const data = await apiGet<{ suppliers?: Supplier[] }>('/suppliers?limit=1000')
      setSuppliers(data.suppliers || [])
    } catch (error) {
      console.error('Error fetching suppliers:', error)
      setSuppliers([])
    } finally {
      setLoading(false)
    }
  }

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCreate = () => {
    setEditingSupplier(null)
    setShowForm(true)
  }

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier)
    setShowForm(true)
  }

  const handleFormSave = async (supplierData: Partial<Supplier>) => {
    try {
      // Обрабатываем пустые строки как null
      const processedData = {
        ...supplierData,
        clubId: supplierData.clubId === '' ? null : supplierData.clubId
      }

      if (editingSupplier) {
        await apiPut(`/suppliers/${editingSupplier.id}`, processedData)
      } else {
        await apiPost('/suppliers', processedData)
      }

      setShowForm(false)
      setEditingSupplier(null)
      fetchSuppliers()
    } catch (error: any) {
      console.error('Error saving supplier:', error)
      alert(error.message || 'Ошибка при сохранении контрагента')
    }
  }

  const handleDeleteClick = (supplier: Supplier) => {
    setSupplierToDelete(supplier)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!supplierToDelete) return

    try {
      await apiDelete(`/suppliers/${supplierToDelete.id}`)
      setShowDeleteConfirm(false)
      setSupplierToDelete(null)
      fetchSuppliers()
    } catch (error: any) {
      console.error('Error deleting supplier:', error)
      alert(error.message || 'Ошибка удаления контрагента')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Контрагенты</h1>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Добавить контрагента
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поиск
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Название, контакт или email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Suppliers List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSuppliers.map(supplier => (
          <div key={supplier.id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-gray-900">{supplier.name}</h3>
                {supplier.club && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {supplier.club.name}
                  </div>
                )}
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                supplier.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {supplier.isActive ? 'Активен' : 'Неактивен'}
              </span>
            </div>

            {supplier.contact && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Контакт:</span> {supplier.contact}
              </p>
            )}

            {supplier.phone && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Телефон:</span> {supplier.phone}
              </p>
            )}

            {supplier.email && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Email:</span> {supplier.email}
              </p>
            )}

            {supplier.address && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Адрес:</span> {supplier.address}
              </p>
            )}

            {supplier.taxId && (
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">ИНН:</span> {supplier.taxId}
              </p>
            )}

            {supplier.documentsCount && (
              <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-center">
                  <div className="text-gray-500">Закупок</div>
                  <div className="font-semibold">{supplier.documentsCount.purchases}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1 text-center">
                  <div className="text-gray-500">Расчетов</div>
                  <div className="font-semibold">{supplier.documentsCount.settlements}</div>
                </div>
              </div>
            )}

            <div className="mt-4 flex space-x-2">
              <button
                onClick={() => handleEdit(supplier)}
                className="flex-1 bg-gray-200 text-gray-800 px-3 py-2 rounded-md text-sm hover:bg-gray-300"
              >
                Редактировать
              </button>
              {/* Показываем кнопку удаления только если нет документов */}
              {(!supplier.documentsCount || 
                (supplier.documentsCount.purchases === 0 && supplier.documentsCount.settlements === 0)) && (
                <button
                  onClick={() => handleDeleteClick(supplier)}
                  className="bg-red-200 text-red-800 px-3 py-2 rounded-md text-sm hover:bg-red-300"
                >
                  Удалить
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredSuppliers.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Контрагенты не найдены</p>
        </div>
      )}

      {/* Supplier Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {editingSupplier ? 'Редактировать контрагента' : 'Добавить контрагента'}
            </h2>
            
            <SupplierForm
              supplier={editingSupplier}
              onSave={handleFormSave}
              onCancel={() => {
                setShowForm(false)
                setEditingSupplier(null)
              }}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && supplierToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Подтверждение удаления</h2>
            <p className="text-gray-600 mb-6">
              Вы уверены, что хотите удалить контрагента "{supplierToDelete.name}"?
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleDeleteConfirm}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
              >
                Удалить
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setSupplierToDelete(null)
                }}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Supplier Form Component
interface SupplierFormProps {
  supplier?: Supplier | null
  onSave: (data: any) => void
  onCancel: () => void
}

const SupplierForm: React.FC<SupplierFormProps> = ({ supplier, onSave, onCancel }) => {
  const [clubs, setClubs] = useState<Array<{ id: string; name: string }>>([])
  const [formData, setFormData] = useState({
    name: supplier?.name || '',
    contact: supplier?.contact || '',
    phone: supplier?.phone || '',
    email: supplier?.email || '',
    address: supplier?.address || '',
    taxId: supplier?.taxId || '',
    bankDetails: supplier?.bankDetails || '',
    isActive: supplier?.isActive ?? true,
    clubId: supplier?.clubId || ''
  })

  useEffect(() => {
    fetchClubs()
  }, [])

  const fetchClubs = async () => {
    try {
      const data = await apiGet<{ clubs?: Array<{ id: string; name: string }> } | Array<{ id: string; name: string }>>('/clubs?isActive=true')
      if (Array.isArray(data)) {
        setClubs(data)
      } else {
        setClubs(data.clubs || [])
      }
    } catch (error) {
      console.error('Error fetching clubs:', error)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Название *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Контактное лицо
          </label>
          <input
            type="text"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Телефон
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Адрес
          </label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ИНН/Налоговый номер
          </label>
          <input
            type="text"
            name="taxId"
            value={formData.taxId}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-900">
            Активен
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Клуб
        </label>
        <select
          name="clubId"
          value={formData.clubId}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Без привязки к клубу</option>
          {clubs.map(club => (
            <option key={club.id} value={club.id}>
              {club.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Банковские реквизиты
        </label>
        <textarea
          name="bankDetails"
          value={formData.bankDetails}
          onChange={handleChange}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
        >
          Отмена
        </button>
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Сохранить
        </button>
      </div>
    </form>
  )
}

export default Suppliers


